import { useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { IASOKeyword } from '~/types/aso'

import columns from './columns'

import ContentLayout from '~/components/ContentLayout'
import Header from '~/components/Header/Classic'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAppSummary } from '~/providers/AppSummary'
import { useAppSummaryDetailsQueryV2 } from '~/queries/applications'
import { useASOKeywordOverview } from '~/queries/aso'
import { transformByDeviceType } from '~/utils/application'

const ASOListing = (): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const gridRef = useRef<AgGridReact<IASOKeyword>>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const { appDetails, appKey, deviceType } = useAppSummary()

  /** Queries & Mutations */

  const { data: appSummary } = useAppSummaryDetailsQueryV2(appKey, deviceType)

  const { data: keywords } = useASOKeywordOverview(appSummary?.app_id)

  /** Effects */

  useDocumentTitle('ASO overview')

  /** Handlers */

  const handleRedirect = (country: string) => {
    const query = new URLSearchParams({ country })
    navigate(`/aso/advanced?${query}`)
  }

  const distinctKeywords = Array.from(
    new Set(keywords?.reduce((prev: string[], { keywords }) => (keywords ? [...prev, ...keywords] : prev), []))
  )

  /** Render */

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  return (
    <>
      <Header apps={appSelectorApp ? [appSelectorApp] : []} isSwitchDisabled={isSwitchDisabled} />
      <Layout className="aso-listing-view" withPadding>
        <ViewTitle className="layout-view-title">ASO overview</ViewTitle>
        <ContentLayout>
          <Section>
            <TableWrapper
              className="aso-listing-table with-min-height"
              columnDefs={columns}
              context={{ distinctKeywords, handleRedirect }}
              entriesName="aso-listing"
              ref={gridRef}
              rowData={keywords}
              rowStyle={{ cursor: 'default' }}
              tableRef={tableRef}
              withPagination={false}
            />
          </Section>
        </ContentLayout>
      </Layout>
    </>
  )
}

export default ASOListing
